export const PRIMARY_NAV = [
  { label: "For You", href: "/sticker-pack/for-you" },
  { label: "Trending", href: "/sticker-pack/trending-stickers" },
  { label: "Newest", href: "/sticker-pack/newest-stickers" },
  { label: "Most Downloaded", href: "/sticker-pack/most-download-stickers" },
  { label: "Most Liked", href: "/sticker-pack/most-like-stickers" },
] as const;

export const CATEGORY_NAV = [
  { label: "TV Shows", href: "/sticker-pack/tv-shows" },
  { label: "Memes", href: "/sticker-pack/memes" },
  { label: "Reactions", href: "/sticker-pack/reactions" },
  { label: "Emojis", href: "/sticker-pack/emojis" },
  { label: "Love", href: "/sticker-pack/love" },
] as const;

/**
 * Unified feed navigation used by category pills (top of feed). Order and
 * emojis match the Sticko Angular reference.
 */
export const FEED_NAV: ReadonlyArray<{ emoji: string; label: string; href: string }> = [
  { emoji: "🙍", label: "For You", href: "/sticker-pack/for-you" },
  { emoji: "🔥", label: "Trending", href: "/sticker-pack/trending-stickers" },
  { emoji: "💥", label: "Newest", href: "/sticker-pack/newest-stickers" },
  { emoji: "💗", label: "Most Like", href: "/sticker-pack/most-like-stickers" },
  { emoji: "🚀", label: "Most Download", href: "/sticker-pack/most-download-stickers" },
  { emoji: "📺", label: "TV Shows", href: "/sticker-pack/tv-shows" },
  { emoji: "😎", label: "Memes", href: "/sticker-pack/memes" },
  { emoji: "😲", label: "Reactions", href: "/sticker-pack/reactions" },
  { emoji: "😀", label: "Emojis", href: "/sticker-pack/emojis" },
  { emoji: "❤️", label: "Love", href: "/sticker-pack/love" },
];

/**
 * Sidebar drawer navigation — static/legal pages. Mirrors the Angular
 * reference's `navigation.service.ts`. Feed/category links live in
 * CategoryPills, not the sidebar.
 */
export const SIDEBAR_NAV: ReadonlyArray<{ label: string; href: string }> = [
  { label: "Home", href: "/" },
  { label: "Guides", href: "/guides" },
  { label: "About", href: "/about" },
  { label: "Editorial Standards", href: "/editorial-standards" },
  { label: "Terms Of Use", href: "/terms-of-use" },
  { label: "Content Policy", href: "/content-policy" },
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Cookie Policy", href: "/cookie-policy" },
  { label: "DMCA Policy", href: "/dmca-policy" },
  { label: "Licence", href: "/licence" },
  { label: "Delete Account", href: "/delete-account" },
  { label: "Partner", href: "/partnership-proposal" },
  { label: "Contact Us", href: "/contact-us" },
];

export const FOOTER_NAV: { title: string; links: { label: string; href: string }[] }[] = [
  {
    title: "Discover",
    links: [
      { label: "For You", href: "/sticker-pack/for-you" },
      { label: "Trending", href: "/sticker-pack/trending-stickers" },
      { label: "Newest", href: "/sticker-pack/newest-stickers" },
      { label: "Most Downloaded", href: "/sticker-pack/most-download-stickers" },
      { label: "Most Liked", href: "/sticker-pack/most-like-stickers" },
      // The only sitewide link to the hashtag hub. Without it the hub — and
      // through it ~10,159 tag listings — would be reachable only from the
      // sitemap, which tends to leave a page unindexed.
      { label: "All Hashtags", href: "/hashtag" },
    ],
  },
  {
    title: "Categories",
    links: [
      { label: "TV Shows", href: "/sticker-pack/tv-shows" },
      { label: "Memes", href: "/sticker-pack/memes" },
      { label: "Reactions", href: "/sticker-pack/reactions" },
      { label: "Emojis", href: "/sticker-pack/emojis" },
      { label: "Love", href: "/sticker-pack/love" },
      // Seasonal hub (lib/collections/content.ts). Sitewide, so the hub is
      // crawlable from every page in the weeks before the festival.
      { label: "Diwali Stickers", href: "/collections/diwali-stickers" },
      { label: "Christmas Stickers", href: "/collections/christmas-stickers" },
      { label: "New Year Stickers", href: "/collections/new-year-stickers" },
      { label: "Good Morning Stickers", href: "/collections/good-morning-stickers" },
    ],
  },
  {
    // Sitewide links into the guide hub. These are the only internal links
    // pointing at it from every page, so they are what lets it be crawled and
    // what passes it any internal authority — a hub reachable only from the
    // sitemap tends to stay unindexed.
    // Every guide is listed (17 as of 2026-10). The three that were missing —
    // stickers-on-iphone, animated-whatsapp-stickers and
    // best-whatsapp-sticker-packs — had no sitewide link at all, so their only
    // inbound paths were the hub and the sitemap. A guides test asserts every
    // slug in lib/guides/content.ts appears here.
    title: "Guides",
    links: [
      { label: "Add stickers to WhatsApp", href: "/guides/how-to-add-stickers-to-whatsapp" },
      { label: "Sticker size & format", href: "/guides/whatsapp-sticker-size-and-format" },
      { label: "Make your own stickers", href: "/guides/how-to-make-your-own-whatsapp-stickers" },
      { label: "Animated stickers", href: "/guides/animated-whatsapp-stickers" },
      { label: "Stickers on iPhone", href: "/guides/whatsapp-stickers-on-iphone" },
      { label: "Stickers not working", href: "/guides/whatsapp-stickers-not-working" },
      { label: "Choosing a pack", href: "/guides/best-whatsapp-sticker-packs" },
      { label: "Stickers on WhatsApp Web", href: "/guides/whatsapp-stickers-on-web-and-desktop" },
      { label: "GIF to sticker", href: "/guides/gif-to-whatsapp-sticker" },
      { label: "Sticker copyright", href: "/guides/whatsapp-sticker-copyright" },
      {
        label: "Stickers after a new phone",
        href: "/guides/whatsapp-stickers-missing-after-changing-phone",
      },
      { label: "How sticker search works", href: "/guides/how-whatsapp-sticker-search-works" },
      { label: "Delete sticker packs", href: "/guides/delete-whatsapp-sticker-packs" },
      { label: "Where stickers are stored", href: "/guides/where-whatsapp-stickers-are-stored" },
      { label: "Stickers on other apps", href: "/guides/use-whatsapp-stickers-on-other-apps" },
      { label: "Telegram stickers to WhatsApp", href: "/guides/add-telegram-stickers-to-whatsapp" },
      { label: "Avatar & premium stickers", href: "/guides/whatsapp-avatar-and-premium-stickers" },
      { label: "All guides", href: "/guides" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", href: "/about" },
      // The honest E-E-A-T surface: guides are Organization-authored, so the
      // trust signal is a documented process rather than a byline.
      { label: "Editorial Standards", href: "/editorial-standards" },
      { label: "Contact Us", href: "/contact-us" },
      { label: "Partnership", href: "/partnership-proposal" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms of Use", href: "/terms-of-use" },
      { label: "Privacy Policy", href: "/privacy-policy" },
      { label: "Content Policy", href: "/content-policy" },
      { label: "Cookie Policy", href: "/cookie-policy" },
      { label: "DMCA Policy", href: "/dmca-policy" },
      { label: "License", href: "/licence" },
      { label: "Delete Account", href: "/delete-account" },
    ],
  },
];
